import { forwardRef, useRef, useState } from 'react'
import { useHover } from 'usehooks-ts'

import CopyOutlinedIcon from '@ant-design/icons/CopyOutlined'

import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import { styled } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import TypographyMui from '@mui/material/Typography'

import CopyToClipboard from '@components/CopyToClipboard'

/**
 * Extend MUI Typography with tooltip on overflow.
 */
const Typography = forwardRef(({ copy, lines, ...props }, ref) => {
  // Optimization for when we know the text will overflow
  if (props.noWrap === true || lines > 0) {
    return <Ellipsis ref={ref} lines={lines} {...props} />
  }

  // When 'copy' is a boolean with true value, we copy the children
  const copyBool = copy === true && ['string', 'number'].includes(typeof props.children)
  // When 'copy' is a string or a number, we copy the copy value instead
  const copyValue = ['string', 'number'].includes(typeof copy)

  if (copyBool || copyValue) {
    const value = copyValue ? copy : props.children
    return <Copy ref={ref} value={value} {...props} />
  }

  return <TypographyMui ref={ref} {...props} />
})

const Ellipsis = forwardRef(({ children, lines, sx, ...props }, ref) => {
  const [tooltipEnabled, setTooltipEnabled] = useState(false)

  const handleShouldShow = ({ currentTarget }) => {
    if (currentTarget.scrollWidth > currentTarget.clientWidth || (lines > 0 && currentTarget.scrollHeight > currentTarget.clientHeight)) {
      setTooltipEnabled(true)
    }
  }

  return (
    <Tooltip open={tooltipEnabled} title={children} onClose={() => setTooltipEnabled(false)}>
      <TypographyMui ref={ref} onMouseEnter={handleShouldShow} sx={lines ? [clampStyles(lines), ...[sx].flat()] : sx} {...props}>
        {children}
      </TypographyMui>
    </Tooltip>
  )
})

const clampStyles = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
})

const Copy = forwardRef(({ value, ...props }, ref) => {
  const hoverRef = useRef(null)
  const isHover = useHover(hoverRef)

  return (
    <CopyToClipboard>
      {({ copy }) => (
        <Box
          ref={hoverRef}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            copy(value)
          }}
          sx={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        >
          <TypographyMui ref={ref} {...props} />
          <Fade in={isHover}>
            <CopyIcon />
          </Fade>
        </Box>
      )}
    </CopyToClipboard>
  )
})

const CopyIcon = styled(CopyOutlinedIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 18,
}))

export default Typography
